import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { customerProfilesApi, ticketsApi } from "../services/api";
import { useNotification } from "../context/NotificationContext";
import { parseUtc } from "../utils/utcDate";
import CustomerMemoryPanel from "../components/CustomerMemoryPanel";
import { detailText } from "../utils/apiErrors";
import { ticketStatusLabel } from "../components/ticketStatusVocabulary";

const VENDOR_FIELD = ({ label, value, onChange }) => (
  <div>
    <label className="block text-xs text-gray-500 mb-1">{label}</label>
    <input
      value={value || ""}
      onChange={(e) => onChange(e.target.value || null)}
      placeholder="לא מוגדר"
      className="w-full border rounded-lg px-3 py-2 text-sm"
    />
  </div>
);

const OS_PRESET_KEYS = ["Windows 11", "Windows 10", "Windows Server 2022", "Windows Server 2019", "macOS", "Linux"];

function OsDistributionEditor({ value, onChange }) {
  const dist = value || {};
  const keys = [...new Set([...OS_PRESET_KEYS, ...Object.keys(dist)])];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
      {keys.map((os) => (
        <div key={os} className="flex items-center gap-2">
          <span className="text-xs text-gray-600 truncate flex-1">{os}</span>
          <input
            type="number"
            min="0"
            value={dist[os] ?? ""}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10);
              const next = { ...dist };
              if (isNaN(v) || v === 0) delete next[os];
              else next[os] = v;
              onChange(Object.keys(next).length ? next : null);
            }}
            placeholder="0"
            className="w-16 border rounded px-2 py-1 text-xs text-center"
          />
        </div>
      ))}
    </div>
  );
}

function AssetRow({ asset, onChange, onRemove }) {
  const set = (k, v) => onChange({ ...asset, [k]: v || null });
  return (
    <div className="grid grid-cols-5 gap-2 items-center border rounded-lg px-3 py-2 bg-gray-50 text-sm">
      <input value={asset.name || ""} onChange={(e) => set("name", e.target.value)} placeholder="שם" className="border rounded px-2 py-1 text-xs col-span-2" />
      <input value={asset.type || ""} onChange={(e) => set("type", e.target.value)} placeholder="סוג (PC/Server)" className="border rounded px-2 py-1 text-xs" />
      <input value={asset.ip || ""}   onChange={(e) => set("ip", e.target.value)}   placeholder="IP" className="border rounded px-2 py-1 text-xs font-mono" />
      <button onClick={onRemove} className="text-red-500 hover:text-red-700 text-xs font-bold">✕</button>
    </div>
  );
}

export default function CustomerProfilePage() {
  const { companyId } = useParams();
  const qc = useQueryClient();
  const { addNotification } = useNotification();

  const { data: profile, isLoading, isError, error: loadError } = useQuery({
    queryKey: ["customer-profile", companyId],
    queryFn: () => customerProfilesApi.get(companyId),
  });

  const [form, setForm] = useState(null);
  const [dirty, setDirty] = useState(false);

  // Initialise form once profile loads
  const currentForm = form ?? (profile ? { ...profile } : null);

  const set = (k, v) => {
    setForm((f) => ({ ...(f ?? profile), [k]: v }));
    setDirty(true);
  };

  const saveMutation = useMutation({
    mutationFn: (data) => customerProfilesApi.upsert(companyId, data),
    onSuccess: (saved) => {
      qc.setQueryData(["customer-profile", companyId], saved);
      qc.invalidateQueries({ queryKey: ["customer-profiles-all"] });     // the companies page lists profile completeness from this key
      setForm(null);
      setDirty(false);
      addNotification("סביבת IT נשמרה בהצלחה", "success");
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בשמירת פרופיל הסביבה", "error"),
  });

  const handleSave = () => {
    if (!currentForm) return;
    // Strip id, company_id, company_name — backend derives these
    const { id, company_id, company_name, created_at, updated_at, ...payload } = currentForm;
    saveMutation.mutate(payload);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-20" dir="rtl">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-600" />
      </div>
    );
  }

  // A failed load (403 for a non-admin, 404, network) used to fall through to an EMPTY editable form, which
  // read as "no profile yet" and then failed on save.
  if (isError) {
    return (
      <div className="max-w-4xl mx-auto space-y-4" dir="rtl">
        <Link to="/companies" className="text-sm text-brand-600 hover:underline">← חברות</Link>
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
          {loadError?.response?.status === 403
            ? "אין הרשאה לצפות בסביבת ה-IT של הלקוח (למנהלים בלבד)"
            : `טעינת סביבת ה-IT נכשלה: ${detailText(loadError?.response?.data?.detail, "נסו שוב")}`}
        </div>
      </div>
    );
  }

  const f = currentForm || {};

  return (
    <div className="max-w-4xl mx-auto space-y-6" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Link to="/companies" className="text-sm text-brand-600 hover:underline">← חברות</Link>
          <h1 className="text-2xl font-bold text-gray-800 mt-1">
            סביבת עבודה — {profile?.company_name || "..."}
          </h1>
        </div>
        <button
          onClick={handleSave}
          disabled={!dirty || saveMutation.isPending}
          className="bg-brand-600 hover:bg-brand-700 text-white px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-40 transition-colors"
        >
          {saveMutation.isPending ? "שומר..." : "שמור שינויים"}
        </button>
      </div>

      {saveMutation.isError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
          שגיאה בשמירה: {detailText(saveMutation.error?.response?.data?.detail, "נסה שוב")}
        </div>
      )}

      {/* Microsoft 365 */}
      <SectionCard title="Microsoft 365 / Azure AD">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <VENDOR_FIELD label="Tenant ID" value={f.ms365_tenant_id} onChange={(v) => set("ms365_tenant_id", v)} />
          <VENDOR_FIELD label="דומיין M365" value={f.ms365_domain} onChange={(v) => set("ms365_domain", v)} />
        </div>
      </SectionCard>

      {/* Endpoints */}
      <SectionCard title="תחנות קצה">
        <div className="mb-4">
          <label className="block text-xs text-gray-500 mb-1">מספר תחנות קצה</label>
          <input
            type="number"
            min="0"
            value={f.endpoint_count ?? ""}
            onChange={(e) => set("endpoint_count", e.target.value === "" ? null : parseInt(e.target.value, 10))}
            placeholder="0"
            className="w-32 border rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-2">התפלגות מערכות הפעלה</p>
          <OsDistributionEditor value={f.os_distribution} onChange={(v) => set("os_distribution", v)} />
        </div>
      </SectionCard>

      {/* Vendors */}
      <SectionCard title="ספקים וכלים">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <VENDOR_FIELD label="ספק גיבויים" value={f.backup_vendor}   onChange={(v) => set("backup_vendor", v)} />
          <VENDOR_FIELD label="אנטי-וירוס / EDR" value={f.av_vendor}  onChange={(v) => set("av_vendor", v)} />
          <VENDOR_FIELD label="VPN" value={f.vpn_vendor}               onChange={(v) => set("vpn_vendor", v)} />
          <VENDOR_FIELD label="ספק דואר" value={f.email_provider}      onChange={(v) => set("email_provider", v)} />
        </div>
      </SectionCard>

      {/* Assets */}
      <SectionCard title="נכסים מנוהלים">
        <div className="space-y-2 mb-3">
          {(f.assets || []).map((asset, i) => (
            <AssetRow
              key={i}
              asset={asset}
              onChange={(updated) => {
                const next = [...(f.assets || [])];
                next[i] = updated;
                set("assets", next);
              }}
              onRemove={() => {
                const next = (f.assets || []).filter((_, j) => j !== i);
                set("assets", next.length ? next : null);
              }}
            />
          ))}
        </div>
        <button
          onClick={() => set("assets", [...(f.assets || []), { name: "", type: "", ip: "", os: null }])}
          className="text-sm text-brand-600 hover:underline"
        >
          + הוסף נכס
        </button>
      </SectionCard>

      {/* Notes */}
      <SectionCard title="הערות תשתית">
        <textarea
          value={f.infrastructure_notes || ""}
          onChange={(e) => set("infrastructure_notes", e.target.value || null)}
          rows={5}
          placeholder="כאן ניתן לרשום מידע חופשי על סביבת העבודה: מבנה הרשת, שמות שרתים, ספקי ענן, הגדרות מיוחדות..."
          className="w-full border rounded-lg px-3 py-2 text-sm resize-y"
        />
      </SectionCard>

      {/* Meta */}
      {profile?.created_at && (
        <p className="text-xs text-gray-400 text-left">
          נוצר: {parseUtc(profile.created_at).toLocaleDateString("he-IL")}
          {profile.updated_at && ` · עדכון אחרון: ${parseUtc(profile.updated_at).toLocaleDateString("he-IL")}`}
        </p>
      )}

      {/* Long-term memory the AI Technician may be shown about this customer (needs agents_view) */}
      <CustomerMemoryPanel companyId={companyId} />

      {/* Recent tickets */}
      <RecentTickets companyId={companyId} />
    </div>
  );
}

function RecentTickets({ companyId }) {
  const { data: tickets = [], isError } = useQuery({
    queryKey: ["company-tickets", companyId],
    queryFn: () => ticketsApi.list({ company_id: companyId, limit: 10 }),
    staleTime: 60000,
  });

  if (isError) {
    return (
      <div role="alert" className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
        לא ניתן לטעון את הקריאות האחרונות של הלקוח
      </div>
    );
  }
  if (!tickets.length) return null;

  // Badge colours only; the wording comes from ticketStatusVocabulary. This list used to carry its
  // own ticket-status words -- four of them borrowed from the maintenance/project QUEUE scale
  // ("לא הוקצה" / "פתוח" / "סגור" / "מוזג", none of which this app uses for a ticket elsewhere) plus
  // one key TicketStatus cannot mint at all ("pending_customer").
  const STATUS_CLS = {
    open: "bg-blue-50 text-blue-700 border-blue-200",
    in_progress: "bg-amber-50 text-amber-700 border-amber-200",
    closed: "bg-gray-100 text-gray-500 border-gray-200",
    waiting: "bg-purple-50 text-purple-700 border-purple-200",
    pending: "bg-purple-50 text-purple-700 border-purple-200",
    unassigned: "bg-gray-100 text-gray-600 border-gray-200",
  };

  return (
    <SectionCard title={`קריאות תמיכה אחרונות (${tickets.length})`}>
      <div className="divide-y divide-gray-100 -mx-6 -mb-6">
        {tickets.map((t) => (
          <div key={t.id} className="flex items-center justify-between gap-3 px-6 py-2.5 hover:bg-gray-50">
            <div className="min-w-0 flex-1">
              <Link
                to={`/tickets/${t.id}`}
                className="text-sm text-gray-800 hover:text-brand-600 hover:underline font-medium truncate block"
              >
                #{t.ticket_number} {t.title}
              </Link>
              {t.sender_name && (
                <p className="text-xs text-gray-400 truncate">{t.sender_name}</p>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {t.is_sla_breach && (
                <span className="text-[10px] bg-red-50 text-red-600 border border-red-200 rounded px-1.5 py-0.5">SLA</span>
              )}
              <span className={`text-[10px] border rounded px-1.5 py-0.5 ${STATUS_CLS[t.status] || "bg-gray-100 text-gray-500 border-gray-200"}`}>
                {ticketStatusLabel(t.status)}
              </span>
            </div>
          </div>
        ))}
      </div>
      <Link
        to={`/tickets?company_id=${companyId}&status=all`}
        className="block text-center text-xs text-brand-600 hover:underline pt-3 pb-1"
      >
        כל הקריאות של לקוח זה →
      </Link>
    </SectionCard>
  );
}

function SectionCard({ title, children }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border p-6 space-y-4">
      <h2 className="font-semibold text-gray-700 border-b pb-2">{title}</h2>
      {children}
    </div>
  );
}
